import { useState } from 'react';

const sameSet = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort((x, y) => x - y);
    const sortedB = [...b].sort((x, y) => x - y);
    return sortedA.every((value, index) => value === sortedB[index]);
};

/**
 * Holds a draft role selection for panels that commit on Save rather than per toggle.
 *
 * `committedIds` is a fresh array on every render, so everything compares by value.
 * A new committed set is adopted only while the draft is clean - a refetch or a
 * different user reinitialises the pane, but an edit in progress is never
 * silently overwritten.
 */
export default function useStagedRoles(committedIds: number[]) {
    const [draftIds, setDraftIds] = useState<number[]>(committedIds);
    const [baseline, setBaseline] = useState<number[]>(committedIds);

    if (!sameSet(committedIds, baseline)) {
        const wasClean = sameSet(draftIds, baseline);
        setBaseline(committedIds);
        if (wasClean) {
            setDraftIds(committedIds);
        }
    }

    const added = draftIds.filter((id) => !committedIds.includes(id));
    const removed = committedIds.filter((id) => !draftIds.includes(id));

    return {
        draftIds,
        setDraftIds,
        added,
        removed,
        isDirty: !sameSet(draftIds, committedIds),
        reset: () => setDraftIds(committedIds)
    };
}
